import React, { useCallback, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useIntl } from "react-intl";
import SweetAlert from "sweetalert2";
import { createFileDownloadFromRawData } from "../../../../lib/supportFunctions";
import { useBackofficeClient } from "../../../../lib/http";
import { StyledSpinner } from "../../../witadmin/CRUD/EntityForm/EntityForm";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { ExperimentEntity, ExperimentStatus } from "../domain/experiment.entity";
import { ExperimentBookingsApi } from "../infrastructure/ExperimentBookings.api";
import { messages } from "../experiment.messages";

// US-024 created this file with only the metrics-export button below. US-025 extends this same
// file in place with a sibling "Exportar detalles de reservas" button — the props interface is
// widened additively rather than the component being split into two files.
export interface ExperimentExportButtonsProps {
  experiment: ExperimentEntity;
  repository: ExperimentRepository;
  // US-025: required for the bookings-export REST call's path segment and `X-Property` header —
  // not derivable from `experiment`, since `ExperimentEntity` has no property-ticker field.
  propertyTicker: string;
}

export function ExperimentExportButtons({
  experiment,
  repository,
  propertyTicker,
}: ExperimentExportButtonsProps) {
  const intl = useIntl();
  const [isExporting, setIsExporting] = useState(false);
  const [exportBookingsLoading, setExportBookingsLoading] = useState(false);
  const [exportSeriesLoading, setExportSeriesLoading] = useState(false);
  // backoffice-manager, not witbooking-api: both CSVs now come from the same warehouse-backed
  // service as the rest of this screen, so the exported numbers and the displayed ones cannot drift.
  const httpClient = useBackofficeClient();
  const experimentBookingsApi = useMemo(() => new ExperimentBookingsApi(httpClient), [httpClient]);

  const handleExportMetrics = useCallback(async () => {
    setIsExporting(true);
    try {
      const csv = await repository.exportMetricsCsv(experiment.id);
      // `csv` already carries the backend's UTF-8 BOM (U+FEFF) as its first character — passed
      // through verbatim, never re-prefixed with a BOM.
      // Filename is intentionally extension-less: createFileDownloadFromRawData appends
      // `.${format || "csv"}` itself.
      createFileDownloadFromRawData(csv, `${experiment.ticker}_metricas`, "text/csv");
    } catch (error) {
      console.error("Error exporting experiment metrics", error);
      // DRAFT/never-started experiments are a predictable, always-failing case (the backend
      // rejects with INVALID_ARGUMENT because `startedAt == null`) — detected here via the
      // already-known `experiment.status` prop, not by parsing the gRPC error code. Every other
      // rejection (network failure, any other gRPC error, any other status) falls back to the
      // generic `export.error` copy. The button itself is never disabled for DRAFT — this is
      // purely a post-click message choice, not a pre-click guard.
      const isNeverStartedExperiment =
        experiment.status === ExperimentStatus.EXPERIMENT_STATUS_DRAFT ||
        experiment.status === ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED;
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(
          isNeverStartedExperiment ? messages.export.errorDraft : messages.export.error
        ),
      });
    } finally {
      setIsExporting(false);
    }
  }, [repository, experiment.id, experiment.ticker, experiment.status, intl]);

  // REST rather than gRPC, to avoid the gRPC-web message-size limit on a potentially large
  // bookings dataset — but now against backoffice-manager's own endpoint, addressed by experiment
  // id (the old route took a ticker).
  const onExportBookings = useCallback(async () => {
    setExportBookingsLoading(true);
    try {
      const csv = await experimentBookingsApi.exportBookings(propertyTicker, experiment.id);
      // The resolved CSV string is passed through unmodified — no parsing, splitting, or
      // row-count computation (AC5). Filename is intentionally extension-less with an explicit
      // "csv" format argument: createFileDownloadFromRawData always appends `.${format || "csv"}`
      // to the filename, so passing a filename that already ends in `.csv` would produce a
      // double-extension bug (see design.md's "double-extension" decision).
      createFileDownloadFromRawData(csv, `${experiment.ticker}_reservas`, "text/csv", "csv");
    } catch (error) {
      console.error("Error exporting experiment bookings", error);
      // No status-specific messaging (401/403/500/network failure all share the same generic
      // toast) — reuses US-024's existing `export.error` key verbatim, no separate
      // `bookingsError` key is introduced (resolved Open Question 2).
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(messages.export.error),
      });
    } finally {
      setExportBookingsLoading(false);
    }
  }, [experimentBookingsApi, propertyTicker, experiment.id, experiment.ticker, intl]);

  // The daily aggregated series behind the evolution chart, with its confidence bounds — the one
  // export that lets someone reproduce the chart (or the verdict) outside the backoffice.
  const onExportSeries = useCallback(async () => {
    setExportSeriesLoading(true);
    try {
      const csv = await experimentBookingsApi.exportSeries(propertyTicker, experiment.id);
      createFileDownloadFromRawData(csv, `${experiment.ticker}_serie`, "text/csv", "csv");
    } catch (error) {
      console.error("Error exporting experiment series", error);
      await SweetAlert.fire({
        icon: "error",
        title: intl.formatMessage(messages.export.error),
      });
    } finally {
      setExportSeriesLoading(false);
    }
  }, [experimentBookingsApi, propertyTicker, experiment.id, experiment.ticker, intl]);

  // Text links, not outline buttons: these now sit in the "Todas las métricas" header row, where
  // three pills would out-shout the section title they belong to. `variant="link"` keeps the
  // accessible button semantics (and the disabled-while-exporting behaviour) intact.
  return (
    <div className="d-flex align-items-center flex-wrap">
      <Button
        variant="link"
        size="sm"
        className="p-0"
        onClick={handleExportMetrics}
        disabled={isExporting}>
        {isExporting && <Spinner animation="border" size="sm" className="mr-2" />}
        {intl.formatMessage(
          isExporting ? messages.results.exportingMetrics : messages.results.exportMetrics
        )}
      </Button>
      <Button
        variant="link"
        size="sm"
        onClick={onExportBookings}
        disabled={exportBookingsLoading}
        className="p-0 ml-3">
        {exportBookingsLoading && <StyledSpinner className="fa fa-spinner fa-spin mr-2" />}
        {intl.formatMessage(messages.export.bookings)}
      </Button>
      <Button
        variant="link"
        size="sm"
        onClick={onExportSeries}
        disabled={exportSeriesLoading}
        className="p-0 ml-3">
        {exportSeriesLoading && <StyledSpinner className="fa fa-spinner fa-spin mr-2" />}
        {intl.formatMessage(messages.results.exportSeries)}
      </Button>
    </div>
  );
}
